import Button from '@ui/Button';
import { blobToBase64, IMAGE_PATH } from '@util/index';
import { uploadString, getDownloadURL, ref } from 'firebase/storage';
import { ProductDocument } from 'models/product';
import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useCreateListing } from '../_context';
import { storage } from '@util/firebase';
import AddVideoIcon from '../_assets/AddVideoIcon';
import Spinner from '@ui/Spinner';

export const VideoSelect = () => {
  const { form, product } = useCreateListing();
  const [uploadingVideo, setUploadingVideo] = useState(false);

  function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files[0]) {
      const isVideoFile = e.target.files[0].type.includes('video');

      if (!isVideoFile) {
        return;
      }

      uploadVideo(e.target.files[0]).then((remotePath) => {
        if (remotePath) {
          form.setValue('video', remotePath);
        }
      });
    }
  }

  const getImgPath = useCallback(() => {
    return (
      product.seller_id + '/products/' + product.id + '/image' + Date.now()
    );
  }, [product.seller_id, product.id]);

  const uploadMedia = useCallback(
    async (file: File): Promise<ProductDocument['video']> => {
      let path = getImgPath();

      if (!file || !path)
        return {
          download_url: '',
          mime_type: '',
          thumb: '',
          full: '',
        };

      if (file.name?.startsWith('http'))
        return {
          download_url: file.name,
          mime_type: file.type,
          thumb: file.name,
          full: file.name,
        };

      const storageRef = ref(storage, path);
      const b64 = await blobToBase64(file);
      const res = await uploadString(
        storageRef,
        b64.split('base64,')[1],
        'base64',
        {
          contentType: file.type,
        }
      );

      let cdnUrl = IMAGE_PATH + res.metadata.fullPath;
      let downloadUrl = '';

      try {
        downloadUrl = await getDownloadURL(res.ref);
      } catch (e) {
        downloadUrl = cdnUrl;
      }

      return {
        download_url: downloadUrl,
        mime_type: file.type,
        full: cdnUrl,
        thumb: cdnUrl,
      };
    },
    [getImgPath]
  );

  const uploadVideo = useCallback(
    async (file: File) => {
      setUploadingVideo(true);
      try {
        return await uploadMedia(file);
      } finally {
        setUploadingVideo(false);
      }
    },
    [uploadMedia]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const isVideoFile = acceptedFiles[0].type.includes('video');

      if (!isVideoFile) {
        form.setError('video', {
          type: 'manual',
          message: 'Only videos are allowed',
        });
        return;
      }

      uploadVideo(acceptedFiles[0]).then((remotePath) => {
        if (remotePath) {
          form.setValue('video', remotePath);
        }
      });
    },
    [form, uploadVideo]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  return (
    <div className="flex flex-col gap-4">
      {!product.video && (
        <div
          className="flex w-full grow cursor-pointer gap-[6rem]"
          id="product-video"
        >
          <div className="flex grow flex-col gap-[1.6rem]">
            <div
              {...getRootProps()}
              className="flex h-[20rem] w-[40rem] items-center justify-center rounded-[1rem] border-2 border-dashed border-zinc-300 py-16 sm:w-[25rem]"
            >
              <div className="flex flex-col items-center gap-y-4">
                {uploadingVideo ? (
                  <div className="flex h-full w-full items-center justify-center">
                    <div className="flex items-center justify-center">
                      <Spinner className="!h-32 !w-32" />
                    </div>
                  </div>
                ) : (
                  <>
                    <AddVideoIcon className="h-20 w-20 text-zinc-500" />
                    <div className="flex flex-col gap-y-2">
                      <span className="text-center text-[1.8rem] font-semibold text-zinc-600">
                        {isDragActive ? 'Drop the video here' : 'Upload video'}
                      </span>
                      {!isDragActive && (
                        <span className="text-center text-[1.8rem] text-zinc-600">
                          or drag and drop
                        </span>
                      )}
                    </div>

                    <input
                      {...getInputProps()}
                      onChange={handleFileInputChange}
                      type="file"
                      accept="video/*"
                      className="hidden"
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {form.formState.errors?.images &&
        form.formState.errors?.video?.message && (
          <p className="text-red-500">
            {form.formState.errors?.video?.message}
          </p>
        )}

      {product.video?.download_url && (
        <div className="flex flex-col justify-between gap-[1.6rem] lg:w-1/3">
          <div className="relative aspect-square h-[30rem] w-[30rem] overflow-hidden rounded-[1.6rem] bg-brand-darker-white">
            <video
              key={product.video.download_url}
              className="absolute inset-0 h-full w-full object-cover"
              src={product.video.download_url}
              controls
            />
          </div>

          <div className="flex w-[30rem] justify-end">
            <Button
              onClick={() => form.setValue('video', undefined)}
              text="Remove"
            />
          </div>
        </div>
      )}
    </div>
  );
};
